import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Accordion from '../components/Accordion';
import { useAPI } from '../hooks/useAPI';
import { contentAPI } from '../services/api';
import { getImageUrl } from '../config/api';

const Home = () => {
  const { data: content, loading, error } = useAPI(contentAPI.getHomeContent, []);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Rotate background image every 6 seconds
  useEffect(() => {
    if (!content?.hero?.backgroundImages || content.hero.backgroundImages.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % content.hero.backgroundImages.length
      );
    }, 6000);

    return () => clearInterval(interval);
  }, [content?.hero?.backgroundImages]);

  // Show loading state
  if (loading) {
    return (
      <>
        <Header variant="dark" />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-teal-900 mx-auto mb-4"></div>
            <p className="text-xl text-gray-700">{content?.messages?.loading || 'Loading content...'}</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Show error state
  if (error) {
    return (
      <>
        <Header variant="dark" />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md">
            <h2 className="text-2xl font-bold text-red-600 mb-4">{content?.messages?.error || 'Error Loading Content'}</h2>
            <p className="text-gray-700 mb-4">{error}</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Render content from API
  return (
    <>
      <div>
        <section 
          className="relative bg-teal-900 bg-cover bg-center transition-all duration-1000"
          style={{
            backgroundImage: content?.hero?.backgroundImages && content.hero.backgroundImages.length > 0
              ? `linear-gradient(rgba(2, 44, 34, 0.7), rgba(2, 44, 34, 0.7)), url('${getImageUrl(content.hero.backgroundImages[currentImageIndex])}')`
              : 'none',
          }}
        >
          <Header variant="dark" />
          
          <div className="relative pt-18 pb-24 sm:pb-32 lg:pt-36 lg:pb-62">
            <div className="container mx-auto px-4 relative">
              <div className="max-w-lg xl:max-w-xl mx-auto text-center">
                <h1 className="font-heading text-5xl xs:text-7xl xl:text-8xl tracking-tight text-white mb-8 relative z-10">
                  {content?.hero?.title ? (
                    <div dangerouslySetInnerHTML={{ __html: content.hero.title }} />
                  ) : (
                    'From Sri Lanka to the World'
                  )}
                </h1>
                <p className="max-w-md xl:max-w-none text-lg text-white opacity-80 mb-10 relative z-10">
                  {content?.hero?.subtitle || 'Island Bridge connects businesses worldwide through seamless export services.'}
                </p>
                <Link className="inline-flex py-4 px-6 items-center justify-center text-lg font-medium text-teal-900 border border-lime-500 hover:border-white bg-lime-500 hover:bg-white rounded-full transition duration-200 relative z-10" to="/what-we-offer">
                  {content?.hero?.buttonText || 'Explore Our Services'}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Stats Section
      <section className="py-12 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap -mx-4">
            {content?.stats?.map((stat, index) => (
              <div key={index} className="w-full sm:w-1/2 md:w-1/4 px-4 mb-10 md:mb-0">
                <div className="text-center">
                  <h5 className="text-2xl xs:text-3xl lg:text-4xl xl:text-5xl mb-4">{stat.value}</h5>
                  <span className="text-base lg:text-lg text-gray-700">{stat.description}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Solutions Section */}
      <section className="py-12 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          {/* <div className="flex mb-8 items-center">
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="4" cy="4" r="4" fill="#022C22"></circle>
            </svg>
            <span className="inline-block ml-2 text-sm font-medium text-gray-600">{content?.solutions?.subtitle || 'Services'}</span>
          </div> */}
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl mb-16 text-teal-900">{content?.solutions?.title || 'What We Offer'}</h1>
          <style>{`
            @media (min-width: 768px) {
              .solutions-grid > :last-child:nth-child(odd) {
                grid-column: 1 / -1;
                justify-self: center;
                width: calc(50% - 0.75rem);
              }
            }
          `}</style>
          <div className="solutions-grid grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {content?.solutions?.items?.map((solution, index) => (
              <div key={index} className="relative group overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 h-96">
                {/* Background Image */}
                {solution.image ? (
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-105"
                    style={{
                      backgroundImage: `url('${getImageUrl(solution.image)}')`,
                    }}
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-900 to-teal-700" />
                )}
                
                {/* Dark Overlay for Text Readability */}
                <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-300 group-hover:bg-opacity-60" />
                
                {/* Content */}
                <div className="relative z-10 h-full flex flex-col justify-end p-8 md:p-10 lg:p-12">
                  <h5 className="text-2xl font-bold mb-3 text-white leading-tight">{solution.title}</h5>
                  <p className="text-base text-gray-100 mb-6 leading-relaxed">{solution.description}</p>
                  <Link className="inline-flex text-base font-semibold text-lime-500 hover:text-white transition-colors duration-200 items-center group/link" to={solution.link || "/what-we-offer"}>
                    {content?.solutions?.learnMore || 'Learn more →'}
                    <span className="ml-2 transform group-hover/link:translate-x-1 transition-transform duration-200">→</span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="py-16 lg:py-24 bg-gradient-to-r from-teal-900 to-teal-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-3xl sm:text-5xl md:text-6xl tracking-sm mb-8 text-white">
              {content?.cta?.title || 'Connecting Businesses Across Continents'}
            </h2>
            <p className="text-lg text-teal-50 mb-12 leading-relaxed">
              {content?.cta?.description || 'Ready to expand your export business? Get started with Island Bridge today and reach new markets with confidence.'}
            </p>
            <Link className="inline-flex py-4 px-8 items-center justify-center text-lg font-medium text-teal-900 bg-white hover:bg-lime-500 hover:text-teal-900 rounded-full transition duration-200 shadow-lg" to={content?.cta?.buttonLink || '/contact'}>
              {content?.cta?.buttonText || 'Get in touch'}
            </Link>
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            {content?.cta?.features?.map((feature, index) => (
              <div key={index} className="bg-white bg-opacity-10 backdrop-blur-sm p-6 rounded-lg border border-white border-opacity-20">
                <div className="text-3xl font-bold text-white mb-2">{feature.title}</div>
                <p className="text-teal-50">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* FAQ Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl mb-4 text-teal-900">{content?.faq?.title || 'Frequently Asked Questions'}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{content?.faq?.subtitle || 'Find answers to common questions about our export services and processes.'}</p>
          </div>
          <Accordion items={content?.faq?.items || []} />
          <div className="max-w-4xl mx-auto mt-12">
            <div className="p-8 md:p-12 bg-teal-900 rounded-xl border-2 border-teal-700">
              <div className="flex flex-col md:flex-row items-start md:items-center">
                <div className="mb-6 md:mb-0 md:mr-8 flex-shrink-0">
                  <div className="flex items-center justify-center w-16 h-16 bg-white rounded-full">
                    <svg className="w-8 h-8 text-teal-900" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zm-11-1a1 1 0 11-2 0 1 1 0 012 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <div className="flex-grow">
                  <h5 className="text-2xl font-bold text-white mb-3">{content?.faq?.supportTitle || 'Still have questions?'}</h5>
                  <p className="text-teal-50 mb-4">
                    {content?.faq?.supportDescription || 'Can\'t find the answer you\'re looking for? Our dedicated support team is here to help. Contact us today and we\'ll get back to you within 24 hours.'}
                  </p>
                  <Link className="inline-flex py-3 px-6 items-center justify-center text-base font-semibold text-teal-900 bg-white hover:bg-gray-100 rounded-lg transition duration-200" to="/contact">
                    {content?.faq?.supportButtonText || 'Contact Support'}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Home;
